import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
} from "reactstrap"
import Select from "react-select";
import { Form, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import { addStaticMaintenanceSchema } from "components/Common/validation"
import {
  addStaticMaintenance,
  getStaticMaintenanceDetails,
  updateStaticMaintenance,
  getStaticMaintenanceList,
} from "store/actions/staticMaintenanceAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import CreatableSelect from "react-select/creatable";
import { dataTypes } from "components/Common/constants"


const initialValues = {
  code: "",
  value: "",
  type: "",
}

// Helper function to convert value to specific type
const convertValueToType = (value, type) => {
  if (!value || !type) return value;
  
  switch (type) {
    case "number":
      return parseInt(value, 10);
    case "fLoat": // Note: there's a typo in the dataTypes constant
      return parseFloat(value);
    case "boolean":
      // Convert string to boolean - treat "true", "1", "yes" as true, everything else as false
      const lowerValue = value.toLowerCase();
      return lowerValue === "true" || lowerValue === "1" || lowerValue === "yes";
    case "text":
    default:
      return value; // Keep as string
  }
}

const AddStaticMaintenance = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Static Maintenance "
  )}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading, staticMaintenancDetails, staticMaintenanceList } = useSelector(
    state => state.StaticMaintenance
  )

  const [formValues, setFormValues] = useState(initialValues)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      41,
      "static_maintenance_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      41,
      "static_maintenance_3",
      accountType
    )

    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  // IF HAVE MODE===EDIT THEN UPDATE INITAIL VALUES
  useEffect(() => {
    if (mode === "edit") {
      getOldValues()
    }
  }, [])

  const getOldValues = () => {
    const payload = {
      id,
    }
    dispatch(getStaticMaintenanceDetails(payload))
  }

  // GET OLD VALUES WHEN HAVE
  useEffect(() => {
    if (staticMaintenancDetails && !loading && mode === "edit") {
      const { _id, code, value,type } =
      staticMaintenancDetails

      setFormValues({
        ...formValues,
        code,
        value,
        type
      })
    }
  }, [staticMaintenancDetails])

  const handleOnSubmit = values => {
    const { code, value, type} = values

    // Convert value to its specific type before sending to API
    const convertedValue = convertValueToType(value, type);

    let payload = {
      "code": code,
      "value": convertedValue,
      "type": type
    }

    if (mode === "add") {
      dispatch(addStaticMaintenance(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/static-maintenance`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      payload.id = id;
      dispatch(updateStaticMaintenance(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/static-maintenance`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {
        })
    }
  }

  useEffect(() => {
    // Fetch Static Maintenance when component mounts
    dispatch(getStaticMaintenanceList());
  }, []);

  const codeOptions = [
    ...new Set(staticMaintenanceList?.map(obj => obj.code))
  ].map(code => ({
    value: code,
    label: code
  })) || [];

  const typeOptions = [
    ...dataTypes.map(item => ({
      label: item.label,
      value: item.value,
    })),
  ];

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")}{" "}
                  {t("Static Maintenance")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addStaticMaintenanceSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="code">
                              <span className="text-danger">*</span> {t("Code")}
                            </label>
                            <CreatableSelect
                              name="code"
                              value={
                                codeOptions?.find(
                                  ({ value }) => value == values?.code
                                ) ||
                                (values?.code
                                  ? { value: values.code, label: values.code }
                                  : null)
                              }
                              onChange={({ value }) =>
                                setFieldValue("code", value)
                              }
                              options={codeOptions}
                              placeholder={t("Select Code")}
                              className="select2-selection"
                              classNamePrefix="select2"
                              isSearchable
                              styles={{
                                control: base => ({
                                  ...base,
                                  minHeight: "38px",
                                  height: "38px",
                                }),
                                valueContainer: base => ({
                                  ...base,
                                  height: "38px",
                                  padding: "0 8px",
                                }),
                                input: base => ({
                                  ...base,
                                  margin: "0px",
                                }),
                                indicatorsContainer: base => ({
                                  ...base,
                                  height: "38px",
                                }),
                              }}
                            />
                            <TextError name="code" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="value">
                              <span className="text-danger">*</span>{" "}
                              {t("Value")}
                            </label>
                            <Field
                              type="text"
                              id="value"
                              name="value"
                              placeholder={t("Value")}
                              className={`form-control ${
                                touched.value && errors.value
                                  ? "is-invalid"
                                  : ""
                              }`}
                              onChange={e => {
                                const inputValue = e.target.value
                                const selectedType = values.type

                                // Basic input filtering based on type
                                let filteredValue = inputValue
                                if (selectedType === "number") {
                                  // Only allow digits, no signs allowed
                                  filteredValue = inputValue.replace(
                                    /[^0-9]/g,
                                    ""
                                  )
                                } else if (selectedType === "fLoat") {
                                  // Allow digits and one decimal point, no signs allowed
                                  filteredValue = inputValue.replace(
                                    /[^0-9.]/g,
                                    ""
                                  )
                                  // Ensure only one decimal point
                                  const parts = filteredValue.split(".")
                                  if (parts.length > 2) {
                                    filteredValue =
                                      parts[0] + "." + parts.slice(1).join("")
                                  }
                                }

                                setFieldValue("value", filteredValue)
                              }}
                            />
                            <TextError name="value" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="type">
                              <span className="text-danger">*</span> {t("Type")}
                            </label>
                            <Select
                              name="type"
                              value={typeOptions?.find(
                                ({ value }) => value == values?.type
                              )}
                              onChange={({ value }) =>
                                setFieldValue("type", value)
                              }
                              options={typeOptions}
                              placeholder={t("Select Type")}
                              className="select2-selection"
                              e
                              classNamePrefix="select2"
                              isSearchable
                              styles={{
                                control: base => ({
                                  ...base,
                                  minHeight: "38px",
                                  height: "38px",
                                }),
                                valueContainer: base => ({
                                  ...base,
                                  height: "38px",
                                  padding: "0 8px",
                                }),
                                input: base => ({
                                  ...base,
                                  margin: "0px",
                                }),
                                indicatorsContainer: base => ({
                                  ...base,
                                  height: "38px",
                                }),
                              }}
                            />
                            <TextError name="type" />
                          </div>
                        </div>

                        <div className="mt-3"></div>
                        <div className="col-12 d-flex justify-content-end">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddStaticMaintenance
